import { z } from "zod";
import { baseEntity, slugInput, tagsSchema } from "./common.js";

/**
 * The vault holds two shapes behind one filterable grid:
 *  - "note": a markdown file that opens on the desk (has filename + content)
 *  - "tool" | "read" | "reference": an external link (has url)
 */
export const vaultKind = z.enum(["note", "tool", "read", "reference"]);
export type VaultKind = z.infer<typeof vaultKind>;

export const vaultEntry = baseEntity.extend({
  kind: vaultKind,
  title: z.string().min(1).max(200),
  /** For notes: the displayed filename e.g. "docker-cleanup.md". */
  filename: z.string().max(200).nullable(),
  description: z.string().max(600),
  /** Markdown body — present for notes, null for links. */
  content: z.string().nullable(),
  /** External URL — present for links, null for notes. */
  url: z.string().url().max(500).nullable(),
});
export type VaultEntry = z.infer<typeof vaultEntry>;

export const vaultEntryInput = z
  .object({
    kind: vaultKind,
    title: z.string().min(1).max(200),
    filename: z.string().max(200).nullable().default(null),
    description: z.string().max(600).default(""),
    content: z.string().nullable().default(null),
    url: z.string().url().max(500).nullable().default(null),
    slug: slugInput,
    tags: tagsSchema.default([]),
    published: z.boolean().default(true),
    sortOrder: z.number().int().default(0),
  })
  .refine((v) => (v.kind === "note" ? v.content !== null : v.url !== null), {
    message: "notes require content; links require a url",
  });
export type VaultEntryInput = z.infer<typeof vaultEntryInput>;

export const vaultEntryUpdate = z.object({
  kind: vaultKind.optional(),
  title: z.string().min(1).max(200).optional(),
  filename: z.string().max(200).nullable().optional(),
  description: z.string().max(600).optional(),
  content: z.string().nullable().optional(),
  url: z.string().url().max(500).nullable().optional(),
  slug: slugInput,
  tags: tagsSchema.optional(),
  published: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
});
export type VaultEntryUpdate = z.infer<typeof vaultEntryUpdate>;
